/**
 * 🌌 SPACE BACKGROUND - Fond Spatial Animé Épique
 * Étoiles, nébuleuses, particules et effets de warp speed
 * 
 * ⚡ OPTIMISÉ POUR MOBILE - Moins d'animations sur petits écrans
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

// Hook pour détecter mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

// Étoiles scintillantes - OPTIMISÉ
const TwinklingStars = ({ count = 100, isMobile = false }) => {
  // Réduire drastiquement sur mobile
  const actualCount = isMobile ? Math.min(count, 20) : count;
  
  const stars = Array.from({ length: actualCount }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    size: Math.random() * 3 + 1,
    delay: Math.random() * 5,
    duration: Math.random() * 3 + 2
  }));

  // Sur mobile : étoiles statiques (pas d'animation)
  if (isMobile) {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {stars.map((star) => (
          <div
            key={star.id}
            className="absolute rounded-full bg-white"
            style={{
              left: star.left,
              top: star.top,
              width: star.size,
              height: star.size,
              opacity: 0.6
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {stars.map((star) => (
        <motion.div
          key={star.id}
          className="absolute rounded-full"
          style={{
            left: star.left,
            top: star.top,
            width: star.size,
            height: star.size,
            background: star.size > 2 
              ? 'radial-gradient(circle, #fff 0%, rgba(6, 182, 212, 0.8) 50%, transparent 100%)'
              : '#fff'
          }}
          animate={{
            opacity: [0.2, 1, 0.2],
            scale: [1, star.size > 2 ? 1.8 : 1.3, 1],
            boxShadow: star.size > 2 ? [
              '0 0 0px rgba(6, 182, 212, 0)',
              '0 0 10px rgba(6, 182, 212, 0.8)',
              '0 0 0px rgba(6, 182, 212, 0)'
            ] : undefined
          }}
          transition={{
            duration: star.duration,
            delay: star.delay,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );
};

// Nébuleuses colorées flottantes - OPTIMISÉ
const Nebulae = ({ isMobile = false }) => {
  // Sur mobile : nébuleuses statiques et réduites
  if (isMobile) {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute w-[400px] h-[400px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)',
            left: '-10%',
            top: '10%',
            filter: 'blur(40px)'
          }}
        />
        <div
          className="absolute w-[300px] h-[300px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)',
            right: '-5%',
            top: '40%',
            filter: 'blur(40px)'
          }}
        />
      </div>
    );
  }

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Nébuleuse Cyan */}
      <motion.div
        className="absolute w-[800px] h-[800px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, rgba(6, 182, 212, 0.05) 40%, transparent 70%)',
          left: '-20%',
          top: '10%',
          filter: 'blur(60px)'
        }}
        animate={{
          x: [0, 50, 0],
          y: [0, 30, 0],
          scale: [1, 1.1, 1]
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />
      
      {/* Nébuleuse Violet */}
      <motion.div
        className="absolute w-[600px] h-[600px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.05) 40%, transparent 70%)',
          right: '-10%',
          top: '30%',
          filter: 'blur(80px)'
        }}
        animate={{
          x: [0, -40, 0],
          y: [0, 50, 0],
          scale: [1, 1.15, 1]
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />
      
      {/* Nébuleuse Rose */}
      <motion.div
        className="absolute w-[500px] h-[500px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(236, 72, 153, 0.15) 0%, rgba(236, 72, 153, 0.03) 40%, transparent 70%)',
          left: '30%',
          bottom: '10%',
          filter: 'blur(70px)'
        }}
        animate={{
          x: [0, 30, -20, 0],
          y: [0, -40, 0],
          scale: [1, 1.2, 1]
        }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 4 }}
      />

      {/* Nébuleuse Or (accent épicerie) */}
      <motion.div
        className="absolute w-[400px] h-[400px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(251, 191, 36, 0.1) 0%, rgba(251, 191, 36, 0.02) 40%, transparent 70%)',
          right: '20%',
          bottom: '20%',
          filter: 'blur(50px)'
        }}
        animate={{
          x: [0, -20, 20, 0],
          y: [0, 20, -20, 0],
          scale: [1, 1.1, 0.95, 1]
        }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut", delay: 6 }}
      />
    </div>
  );
};

// Lignes de vitesse lumière (Warp Speed Effect) - DÉSACTIVÉ SUR MOBILE
const WarpLines = ({ isMobile = false }) => {
  // Désactiver complètement sur mobile
  if (isMobile) return null;
  
  const lines = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    angle: (i / 30) * 360,
    length: Math.random() * 100 + 50,
    delay: Math.random() * 2
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
      {lines.map((line) => (
        <motion.div
          key={line.id}
          className="absolute left-1/2 top-1/2"
          style={{
            width: '2px',
            height: `${line.length}px`,
            background: 'linear-gradient(to bottom, transparent, rgba(6, 182, 212, 0.8), transparent)',
            transformOrigin: '50% 0%',
            transform: `rotate(${line.angle}deg) translateY(-${200 + Math.random() * 300}px)`
          }}
          animate={{
            opacity: [0, 0.8, 0],
            scaleY: [0.5, 1.5, 0.5]
          }}
          transition={{
            duration: 3,
            delay: line.delay,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );
};

// Grille holographique de fond - DÉSACTIVÉ SUR MOBILE
const HolographicGrid = ({ isMobile = false }) => {
  if (isMobile) return null;
  
  return (
    <div 
      className="absolute inset-0 pointer-events-none opacity-[0.03]"
      style={{
        backgroundImage: `
          linear-gradient(rgba(6, 182, 212, 0.5) 1px, transparent 1px),
          linear-gradient(90deg, rgba(6, 182, 212, 0.5) 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px',
        perspective: '500px',
        transform: 'rotateX(60deg) translateY(-50%)',
        transformOrigin: '50% 0%'
      }}
    />
  );
};

// Composant principal - OPTIMISÉ MOBILE
const SpaceBackground = ({ enableWarp = false, intensity = 'high' }) => {
  const isMobile = useIsMobile();
  
  // Sur mobile : réduire l'intensité
  const starCount = isMobile ? 20 : (intensity === 'high' ? 150 : intensity === 'medium' ? 80 : 40);

  return (
    <div className="fixed inset-0 z-0 overflow-hidden" style={{ background: '#020617' }}>
      {/* Gradient de base */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, #020617 0%, #0a0015 25%, #1e1b4b 50%, #0f172a 75%, #020617 100%)'
        }}
      />
      
      {/* Overlay radial */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(6, 182, 212, 0.1) 0%, transparent 50%)'
        }}
      />

      {/* Effets - Optimisés pour mobile */}
      <Nebulae isMobile={isMobile} />
      <TwinklingStars count={starCount} isMobile={isMobile} />
      {enableWarp && <WarpLines isMobile={isMobile} />}
      <HolographicGrid isMobile={isMobile} />
      
      {/* Vignette */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, transparent 0%, rgba(2, 6, 23, 0.4) 100%)'
        }}
      />
    </div>
  );
};

export default SpaceBackground;
export { TwinklingStars, Nebulae, WarpLines, HolographicGrid };
